// Chart palette pulled from the design-system tokens.
// We import the literal hex values from tokens.json rather than the
// utility-class names so Recharts (which needs concrete SVG fill / stroke
// strings) and Tailwind stay in sync. Add a token here, not a hex.

import tokens from "@/design-system/tokens.json";

const gold = tokens.colors.gold as { highlight: string; DEFAULT: string; shadow: string };
const status = tokens.colors.status as {
  success: string;
  warning: string;
  error: string;
  info: string;
};
const gray = tokens.colors.gray as { mid: string };
const border = tokens.colors.border as { light: string; dark: string };
const onyx = tokens.colors.onyx as { DEFAULT: string };

export const chartTheme = {
  primary: gold.DEFAULT,
  primaryHighlight: gold.highlight,
  primaryShadow: gold.shadow,
  success: status.success,
  warning: status.warning,
  error: status.error,
  info: status.info,
  axis: gray.mid,
  grid: border.light,
  surface: "#ffffff",
  subtle: "#F5F2EA",
  onyx: onyx.DEFAULT,
};

/**
 * Ordered palette for categorical series (e.g. DQR bins, lane breakdowns).
 * Keep the count = 5 so the DQR / lane charts can map 1:1.
 */
export const CATEGORICAL: readonly string[] = [
  chartTheme.success,
  chartTheme.info,
  chartTheme.primary,
  chartTheme.warning,
  chartTheme.error,
];

export function heatmapColor(value: number, max: number): string {
  // Interpolate between subtle (cold) and primary (hot) on a 0..1 scale.
  if (max <= 0) return chartTheme.subtle;
  const t = Math.max(0, Math.min(1, value / max));
  const blend = (a: string, b: string) => {
    const ah = parseInt(a.slice(1), 16);
    const bh = parseInt(b.slice(1), 16);
    const ar = (ah >> 16) & 0xff, ag = (ah >> 8) & 0xff, ab = ah & 0xff;
    const br = (bh >> 16) & 0xff, bg = (bh >> 8) & 0xff, bb = bh & 0xff;
    const mix = (x: number, y: number) => Math.round(x + (y - x) * t);
    const out = (mix(ar, br) << 16) | (mix(ag, bg) << 8) | mix(ab, bb);
    return `#${out.toString(16).padStart(6, "0")}`;
  };
  return blend(chartTheme.subtle, chartTheme.primary);
}
